import React from 'react';
import { am5, am5themes_Animated, am5xy, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import { createAxes } from './methods/create-axes';
import { createSeries } from './methods/create-series';
import { getScaledData } from './methods/get-scaled-data';
import { addLabels } from '../shared/add-labels';
import { createBaseLegend } from '../shared/create-base-legend';
import { createBaseTooltips } from '../shared/create-base-tooltips';
import { createDownloadButton } from '../shared/create-download-button';
import { handleZoom } from '../shared/handle-zoom';
import { getPaddingLeft } from '../shared/helpers';

export type ChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  xAxisField: string;
  yAxisField: string;
  percent: boolean;
  bucket: string | undefined;
  showError: boolean;
  showBucketOnTooltip: boolean;
  yAxisLabel: string | undefined;
  xAxisLabel: string | undefined;
  download: boolean;
  formatData?: (_data: types.widgets.ErrorLinearData) => types.widgets.ErrorLinearData;
};

const Chart = ({
  id,
  data,
  fullScreen,
  xAxisField,
  yAxisField,
  percent,
  bucket,
  showError,
  showBucketOnTooltip,
  yAxisLabel,
  xAxisLabel,
  download,
  formatData,
}: ChartProps) => {
  const widgetId = `chart-${id}-${fullScreen ? 'fs' : 'sw'}`;
  const theme = mui.styles.useTheme() as mui.core.Theme;

  React.useEffect(() => {
    // Create root element
    const root = am5.Root.new(widgetId);

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Remove amCharts logo
    root._logo?.dispose();

    const [errorData, minLimit, maxLimit] = getScaledData(
      data as types.widgets.TableData,
      xAxisField,
      yAxisField,
      showError ? 'error' : undefined,
      bucket,
      !percent
    );

    let formattedErrorData = errorData;
    if (formatData) formattedErrorData = formatData(errorData);

    const dataLength = Object.keys(formattedErrorData ?? {}).length;
    const shouldShowLegend = dataLength > 1 && (fullScreen || dataLength <= 6);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelY: 'zoomX',
        wheelX: 'panX',
        pinchZoomX: false,
        layout: root.verticalLayout,
        maxTooltipDistance: 0,
        paddingLeft: getPaddingLeft(download, yAxisLabel),
        paddingBottom: shouldShowLegend ? 0 : 20,
      })
    );

    const { xAxis, yAxis } = createAxes({
      root,
      chart,
      minLimit,
      maxLimit,
    });

    createSeries({
      root,
      chart,
      data: formattedErrorData,
      xAxisField,
      yAxisField,
      xAxis,
      yAxis,
      showBucketOnTooltip,
      percent,
      showError,
    });

    chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        xAxis: xAxis,
        yAxis: yAxis,
      })
    );

    if (shouldShowLegend) createBaseLegend({ root, chart, yAxis });
    createBaseTooltips({ root, chart, xAxis, yAxis, theme });
    handleZoom(chart);
    if (download) createDownloadButton(root, xAxisField, data);
    addLabels({ root, yAxis, xAxis, yAxisLabel, xAxisLabel });

    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [data, showError]);

  return <div id={widgetId} style={{ width: '100%', height: '100%' }} />;
};

export default Chart;
